import {isMissingPrescribedName} from '@/modules/patients-therapies/functions'
import {useSequenceValidation} from '@/modules/patients-therapies/hooks'
import {useIsValidationBlockedByGfr} from '@/modules/patients-therapies/hooks/use-is-validation-blocked-by-gfr'
import {getParameterDefaultValuesNew} from '@/modules/patients/functions'
import {ParameterSchemaNew} from '@/modules/patients/schemas'
import {Box, Button, ButtonGroup, Text, VStack} from '@chakra-ui/react'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {Tooltip} from '@cypro/ui/components'
import {withForm} from '@cypro/ui/integrations/form'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {ValidatedLine} from './validated-line'
import {checkSequenceOverdose} from '../dialogs/sequence-overdose-check'

// The doctor's validation is already recorded; the pharmacist validates against
// the live form values (products, carrier, volume, notes) in a single step.
export const PharmacistValidation = withForm({
  defaultValues: getParameterDefaultValuesNew() as ParameterSchemaNew,
  props: {} as {
    sequence: PatientTherapySequence
    isSaveDisabled?: boolean
    saveDisabledReason?: string
    onSaveAndValidate?: (validate: () => Promise<void>) => void
  },
  render: function Render({form, sequence, isSaveDisabled = false, saveDisabledReason, onSaveAndValidate}) {
    const {t} = useTranslate('common')
    const {t: tActivity} = useTranslate('activity')
    const {
      canPharmacistValidate: canValidate,
      doctorValidator,
      canRemoveValidation,
      onRemoveValidation,
      removeValidationLabel,
      onPharmacistValidate,
    } = useSequenceValidation(sequence)

    const values = useStore(form.store, state => state.values)
    const isDirty = useStore(form.store, state => !state.isDefaultValue)

    const isValidationBlockedByTradeName = isMissingPrescribedName(values.medicine)
    const isValidationBlockedByGfr = useIsValidationBlockedByGfr(sequence)

    function getDisabledReason() {
      if (!canValidate) return null
      if (isValidationBlockedByTradeName) return t('prescription_tradename_required')
      if (isValidationBlockedByGfr) return t('auc_gfr_required')
      if (isDirty && saveDisabledReason) return t(saveDisabledReason)
      return null
    }

    // Save and validate runs the normal save (form checks, History) first, like the doctor's
    // button, so a blocked save never reaches the validation.
    function onClickValidate() {
      const body = {
        applicationFormId: values.applicationFormId,
        carrierSolutionId: values.carrierSolutionId,
        duration: values.duration,
        volume: values.volume,
        volumeMode: values.volumeMode,
        therapyNote: values.therapyNote,
        pharmacyNote: values.pharmacyNote,
        medicine:
          values.medicine?.filter(m => m.id).map(m => ({id: m.id as string, prescribedNameId: m.prescribedNameId})) ||
          [],
      }

      async function run() {
        // a pristine form skips the save pipeline, but validation still has to respect a hard stop
        if (!isDirty && !(await checkSequenceOverdose(sequence, t))) return

        if (isDirty && onSaveAndValidate) {
          onSaveAndValidate(async () => {
            await onPharmacistValidate(body)
          })
          return
        }

        void onPharmacistValidate(body)
      }

      void run()
    }

    return (
      <Box width="100%">
        <VStack gapY="2" alignItems="flex-start" width="100%">
          {sequence.doctorValidatedAt && (
            <ValidatedLine
              label={t('doctor_validated')}
              name={doctorValidator?.name}
              time={sequence.doctorValidatedAt}
            />
          )}

          <Text fontWeight="medium">{t('ready_for_pharmacist_validation')}</Text>
        </VStack>

        <ButtonGroup
          display="flex"
          flexDir="column"
          gap="2"
          size="md"
          variant="subtle"
          colorPalette="cyan"
          width="100%"
          marginTop="2"
        >
          {canRemoveValidation() && (
            <Button width="100%" onClick={onRemoveValidation}>
              {removeValidationLabel}
            </Button>
          )}

          <Tooltip content={getDisabledReason()}>
            <Button
              disabled={Boolean(getDisabledReason()) || !canValidate || (isDirty && isSaveDisabled)}
              width="100%"
              onClick={onClickValidate}
            >
              {isDirty ? t('save_and_validate') : tActivity('actions.PHARMACIST_VALIDATE')}
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Box>
    )
  },
})
